import React, { useCallback, useEffect, useState } from 'react';
import { Play, Square, Loader2, History, CheckCircle2, MapPin } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Skeleton } from '../ui/skeleton';
import { toast } from 'sonner';
import { easerGet, easerPost, errorMessage, getPosition, fmtTime } from '../../lib/easerApi';
import { EaserHours } from './EaserHours';

const fmtDay = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit', month: '2-digit' });
const duration = (start, end) => {
  if (!start || !end) return '';
  const mins = Math.round((new Date(end) - new Date(start)) / 60000);
  return `${Math.floor(mins / 60)}h${String(mins % 60).padStart(2, '0')}`;
};

export function EaserPointage({ onUnauthorized }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setError('');
    try {
      const res = await easerGet('/api/easer/day');
      setData(res.data);
    } catch (err) {
      if (err?.response?.status === 401) return onUnauthorized();
      setError(errorMessage(err, 'Impossible de charger le pointage'));
      setData({ today: {}, history: [] });
    }
  }, [onUnauthorized]);

  useEffect(() => { load(); }, [load]);

  const clock = async (action) => {
    setBusy(true);
    try {
      const pos = await getPosition();
      await easerPost('/api/easer/day/clock', { action, ...pos });
      toast.success(action === 'start' ? 'Bonne journée ! Début pointé.' : 'Fin de journée pointée. À demain !');
      await load();
    } catch (err) {
      if (err?.response?.status === 401) return onUnauthorized();
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (!data) return <div className="space-y-3"><Skeleton className="h-44 rounded-xl" /><Skeleton className="h-32 rounded-xl" /></div>;

  const { today, history } = data;
  const started = !!today.start;
  const ended = !!today.end;

  return (
    <div className="space-y-4" data-testid="easer-pointage">
      <Card className="p-5 border-border shadow-sm">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">Aujourd'hui</p>
        <h2 className="font-['Space_Grotesk'] text-xl font-semibold capitalize mb-4">{new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}</h2>
        <div className="grid grid-cols-2 gap-3 mb-5">
          <div className="rounded-xl bg-[hsl(var(--secondary))]/60 p-3">
            <p className="text-xs text-muted-foreground">Début</p>
            <p className="text-2xl font-bold text-[hsl(var(--primary))]" data-testid="day-start-time">{fmtTime(today.start)}</p>
          </div>
          <div className="rounded-xl bg-[hsl(var(--secondary))]/60 p-3">
            <p className="text-xs text-muted-foreground">Fin</p>
            <p className="text-2xl font-bold text-[hsl(var(--primary))]" data-testid="day-end-time">{fmtTime(today.end)}</p>
          </div>
        </div>
        {error && <p className="text-sm text-destructive mb-3">{error}</p>}
        {!started && (
          <Button onClick={() => clock('start')} disabled={busy} size="lg" className="w-full h-14 text-base bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white" data-testid="day-clock-start">
            {busy ? <Loader2 className="h-5 w-5 mr-2 animate-spin" /> : <Play className="h-5 w-5 mr-2" />} Pointer le début de journée
          </Button>
        )}
        {started && !ended && (
          <Button onClick={() => clock('end')} disabled={busy} size="lg" className="w-full h-14 text-base bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 text-white" data-testid="day-clock-end">
            {busy ? <Loader2 className="h-5 w-5 mr-2 animate-spin" /> : <Square className="h-5 w-5 mr-2" />} Pointer la fin de journée
          </Button>
        )}
        {started && ended && (
          <p className="flex items-center justify-center gap-2 text-sm font-medium text-[hsl(var(--brand-sage))] py-2" data-testid="day-complete">
            <CheckCircle2 className="h-4 w-4" /> Journée pointée · {duration(today.start, today.end)}
          </p>
        )}
        <p className="text-[11px] text-muted-foreground flex items-center gap-1 mt-3"><MapPin className="h-3 w-3" /> Votre position est jointe au pointage si vous l'autorisez. Chaque pointage est enregistré dans l'agenda Dolibarr.</p>
      </Card>

      <EaserHours onUnauthorized={onUnauthorized} />

      <Card className="p-5 border-border shadow-sm">
        <h3 className="font-['Space_Grotesk'] font-semibold flex items-center gap-2 mb-3"><History className="h-4 w-4" /> Historique des journées</h3>
        {history.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="day-history-empty">Aucun pointage de journée pour le moment.</p>
        ) : (
          <ul className="divide-y divide-border" data-testid="day-history">
            {history.map((h) => (
              <li key={h.day} className="flex items-center justify-between py-2.5 text-sm">
                <span className="capitalize font-medium">{fmtDay(h.day)}</span>
                <span className="text-muted-foreground tabular-nums">{fmtTime(h.start)} → {fmtTime(h.end)}</span>
                <span className="font-semibold text-[hsl(var(--primary))] tabular-nums w-14 text-right">{duration(h.start, h.end)}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
